/**
 * Block registry. Every block is a row in a typed data table so tuning is a
 * data edit, never a code branch. Ids are stable once shipped: save files and
 * chunk bytes store the numeric id, so a shipped id may never be renumbered —
 * add new blocks at the end.
 *
 * `hardness` is seconds to break with bare hands; -1 means the block never
 * breaks (bedrock, water). `toolTier` is the minimum pickaxe tier, 0 = bare
 * hands, 99 = unbreakable. `drop` is the block id left behind when mined;
 * 0 means the block drops nothing. Ores drop themselves for M0 — item drops
 * (raw iron, coal lumps) arrive with the inventory in M1.
 */

export const BLOCK = {
  AIR: 0,
  GRASS: 1,
  DIRT: 2,
  STONE: 3,
  COBBLE: 4,
  LOG: 5,
  LEAVES: 6,
  PLANK: 7,
  SAND: 8,
  GRAVEL: 9,
  BEDROCK: 10,
  COAL_ORE: 11,
  IRON_ORE: 12,
  DEEP_IRON_ORE: 13,
  TORCH: 14,
  LAMP: 15,
  WATER: 16,
  LIMESTONE: 17,
  SHALE: 18,
  DEEPSTONE: 19,
} as const;

export type BlockId = (typeof BLOCK)[keyof typeof BLOCK];

/** Placeholder atlas coordinate: one tile index into a grid the renderer paints. */
export interface AtlasTile {
  readonly u: number;
  readonly v: number;
}

export interface BlockDef {
  readonly id: BlockId;
  readonly name: string;
  readonly hardness: number;
  readonly toolTier: number;
  readonly drop: BlockId | 0;
  /** Atlas tiles per face group. Non-cubic faces (torch) ignore them for now. */
  readonly tiles: { readonly top: AtlasTile; readonly side: AtlasTile; readonly bottom: AtlasTile };
  /** Opaque blocks cull the faces of their neighbours. */
  readonly opaque: boolean;
  /** Solid blocks collide and can be stood on. Water, torch and lamp are not. */
  readonly solid: boolean;
}

function tile(u: number, v: number): AtlasTile {
  return { u, v };
}

function faces(u: number, v: number): BlockDef["tiles"] {
  return { top: tile(u, v), side: tile(u, v), bottom: tile(u, v) };
}

const AIR_DEF: BlockDef = {
  id: BLOCK.AIR,
  name: "air",
  hardness: -1,
  toolTier: 0,
  drop: 0,
  tiles: faces(0, 0),
  opaque: false,
  solid: false,
};

/** Indexed by block id. Keep the array order in sync with `BLOCK` above. */
export const BLOCKS: readonly BlockDef[] = [
  AIR_DEF,
  {
    id: BLOCK.GRASS,
    name: "grass",
    hardness: 0.9,
    toolTier: 0,
    drop: BLOCK.DIRT,
    tiles: { top: tile(0, 0), side: tile(1, 0), bottom: tile(2, 0) },
    opaque: true,
    solid: true,
  },
  { id: BLOCK.DIRT, name: "dirt", hardness: 0.9, toolTier: 0, drop: BLOCK.DIRT, tiles: faces(2, 0), opaque: true, solid: true },
  { id: BLOCK.STONE, name: "stone", hardness: 5, toolTier: 1, drop: BLOCK.COBBLE, tiles: faces(3, 0), opaque: true, solid: true },
  { id: BLOCK.COBBLE, name: "cobble", hardness: 4.5, toolTier: 1, drop: BLOCK.COBBLE, tiles: faces(4, 0), opaque: true, solid: true },
  {
    id: BLOCK.LOG,
    name: "log",
    hardness: 2.2,
    toolTier: 0,
    drop: BLOCK.LOG,
    tiles: { top: tile(6, 0), side: tile(5, 0), bottom: tile(6, 0) },
    opaque: true,
    solid: true,
  },
  { id: BLOCK.LEAVES, name: "leaves", hardness: 0.3, toolTier: 0, drop: 0, tiles: faces(7, 0), opaque: false, solid: true },
  { id: BLOCK.PLANK, name: "plank", hardness: 2, toolTier: 0, drop: BLOCK.PLANK, tiles: faces(0, 1), opaque: true, solid: true },
  { id: BLOCK.SAND, name: "sand", hardness: 0.9, toolTier: 0, drop: BLOCK.SAND, tiles: faces(1, 1), opaque: true, solid: true },
  { id: BLOCK.GRAVEL, name: "gravel", hardness: 0.9, toolTier: 0, drop: BLOCK.GRAVEL, tiles: faces(2, 1), opaque: true, solid: true },
  { id: BLOCK.BEDROCK, name: "bedrock", hardness: -1, toolTier: 99, drop: 0, tiles: faces(3, 1), opaque: true, solid: true },
  { id: BLOCK.COAL_ORE, name: "coal_ore", hardness: 6, toolTier: 1, drop: BLOCK.COAL_ORE, tiles: faces(4, 1), opaque: true, solid: true },
  { id: BLOCK.IRON_ORE, name: "iron_ore", hardness: 7.5, toolTier: 2, drop: BLOCK.IRON_ORE, tiles: faces(5, 1), opaque: true, solid: true },
  { id: BLOCK.DEEP_IRON_ORE, name: "deep_iron_ore", hardness: 8.5, toolTier: 2, drop: BLOCK.DEEP_IRON_ORE, tiles: faces(6, 1), opaque: true, solid: true },
  { id: BLOCK.TORCH, name: "torch", hardness: 0.1, toolTier: 0, drop: BLOCK.TORCH, tiles: faces(7, 1), opaque: false, solid: false },
  { id: BLOCK.LAMP, name: "lamp", hardness: 0.8, toolTier: 0, drop: BLOCK.LAMP, tiles: faces(0, 2), opaque: false, solid: false },
  { id: BLOCK.WATER, name: "water", hardness: -1, toolTier: 0, drop: 0, tiles: faces(1, 2), opaque: false, solid: false },
  { id: BLOCK.LIMESTONE, name: "limestone", hardness: 5.5, toolTier: 1, drop: BLOCK.LIMESTONE, tiles: faces(2, 2), opaque: true, solid: true },
  { id: BLOCK.SHALE, name: "shale", hardness: 6, toolTier: 2, drop: BLOCK.SHALE, tiles: faces(3, 2), opaque: true, solid: true },
  { id: BLOCK.DEEPSTONE, name: "deepstone", hardness: 7, toolTier: 2, drop: BLOCK.DEEPSTONE, tiles: faces(4, 2), opaque: true, solid: true },
];

/** Def for any numeric id. Unknown ids read as air — corrupt bytes must not crash the mesher. */
export function blockDef(id: number): BlockDef {
  return BLOCKS[id] ?? AIR_DEF;
}

export function isOpaque(id: number): boolean {
  return blockDef(id).opaque;
}

export function isSolid(id: number): boolean {
  return blockDef(id).solid;
}
